import type {
  CloudBoardState,
  CloudSchedule,
  CloudSettingsUpdateRequest,
} from "@board-preserve/contracts";

import { scheduleTimeZoneLabel } from "./schedule-timezones";

type SaveState = "failed" | "idle" | "saved" | "saving";

interface BackupSchedulePanelProps {
  browserTimeZone: string;
  cloudState: CloudBoardState;
  dirty: boolean;
  draft: CloudSettingsUpdateRequest;
  error: string | null;
  onCancel: () => void;
  onChange: (draft: CloudSettingsUpdateRequest) => void;
  onComparePlans: () => void;
  onReconnect: () => void;
  onSave: () => void;
  saveState: SaveState;
  testOptionsEnabled: boolean;
  timeZones: readonly string[];
}

const weekdays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

function defaultSchedule(
  frequency: "daily" | "six_hourly" | "twelve_hourly" | "weekly",
  timeZone: string,
): CloudSchedule {
  return frequency === "weekly"
    ? { frequency, localTime: "09:00", timeZone, weekday: "monday" }
    : { frequency, localTime: "09:00", timeZone };
}

export function BackupSchedulePanel({
  browserTimeZone,
  cloudState,
  dirty,
  draft,
  error,
  onCancel,
  onChange,
  onComparePlans,
  onReconnect,
  onSave,
  saveState,
  testOptionsEnabled,
  timeZones,
}: BackupSchedulePanelProps) {
  const schedule = draft.schedule;

  return (
    <section className="schedule-screen">
      {cloudState.settings.scheduleStatus === "action_required" ? (
        <section className="schedule-attention" role="status">
          <div>
            <strong>Automatic backups need attention</strong>
            <p>
              Reconnect Trello access, then save this schedule again. Existing
              backups are unaffected.
            </p>
          </div>
          <button onClick={onReconnect} type="button">
            Reconnect Trello access
          </button>
        </section>
      ) : null}

      <div className="schedule-form">
        <label>
          <span>How often</span>
          <select
            disabled={!cloudState.entitlements.scheduledSnapshots}
            value={
              draft.scheduleTestMinutes === 10
                ? "devstage-10-minutes"
                : schedule.frequency
            }
            onChange={(event) => {
              if (event.currentTarget.value === "devstage-10-minutes") {
                onChange({
                  ...draft,
                  schedule: defaultSchedule("daily", browserTimeZone),
                  scheduleTestMinutes: 10,
                });
                return;
              }
              const frequency = event.currentTarget.value as
                "daily" | "off" | "six_hourly" | "twelve_hourly" | "weekly";
              onChange({
                ...draft,
                schedule:
                  frequency === "off"
                    ? { frequency }
                    : defaultSchedule(frequency, browserTimeZone),
                scheduleTestMinutes: null,
              });
            }}
          >
            <option value="off">Off</option>
            {cloudState.entitlements.availableScheduleFrequencies.includes(
              "six_hourly",
            ) ? (
              <option value="six_hourly">Every 6 hours</option>
            ) : null}
            {cloudState.entitlements.availableScheduleFrequencies.includes(
              "twelve_hourly",
            ) ? (
              <option value="twelve_hourly">Every 12 hours</option>
            ) : null}
            {cloudState.entitlements.availableScheduleFrequencies.includes(
              "daily",
            ) ? (
              <option value="daily">Daily</option>
            ) : null}
            {cloudState.entitlements.availableScheduleFrequencies.includes(
              "weekly",
            ) ? (
              <option value="weekly">Weekly</option>
            ) : null}
            {testOptionsEnabled ? (
              <option value="devstage-10-minutes">
                Every 10 minutes (devstage test)
              </option>
            ) : null}
          </select>
        </label>

        {schedule.frequency !== "off" && draft.scheduleTestMinutes !== 10 ? (
          <label>
            <span>Local time</span>
            <input
              type="time"
              value={schedule.localTime}
              onChange={(event) => {
                onChange({
                  ...draft,
                  schedule: {
                    ...schedule,
                    localTime: event.currentTarget.value,
                  },
                });
              }}
            />
          </label>
        ) : (
          <div className="schedule-field-placeholder" aria-hidden="true" />
        )}

        <label>
          <span>Keep each backup</span>
          <select
            value={
              draft.retentionTestMinutes === 5
                ? "devstage-5-minutes"
                : String(draft.retentionDays)
            }
            onChange={(event) => {
              if (event.currentTarget.value === "devstage-5-minutes") {
                onChange({ ...draft, retentionTestMinutes: 5 });
                return;
              }
              onChange({
                ...draft,
                retentionDays: Number(
                  event.currentTarget.value,
                ) as CloudSettingsUpdateRequest["retentionDays"],
                retentionTestMinutes: null,
              });
            }}
          >
            {testOptionsEnabled ? (
              <option value="devstage-5-minutes">
                5 minutes (devstage test)
              </option>
            ) : null}
            {cloudState.entitlements.retentionDays.map((days) => (
              <option key={days} value={days}>
                {days} {days === 1 ? "day" : "days"}
              </option>
            ))}
          </select>
        </label>

        {schedule.frequency !== "off" && draft.scheduleTestMinutes !== 10 ? (
          <label>
            <span>Timezone</span>
            <select
              value={schedule.timeZone}
              onChange={(event) => {
                onChange({
                  ...draft,
                  schedule: {
                    ...schedule,
                    timeZone: event.currentTarget.value,
                  },
                });
              }}
            >
              {timeZones.map((timeZone) => (
                <option key={timeZone} value={timeZone}>
                  {scheduleTimeZoneLabel(timeZone)}
                  {timeZone === browserTimeZone ? " — this browser" : ""}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <div className="schedule-field-placeholder" aria-hidden="true" />
        )}

        {schedule.frequency === "weekly" ? (
          <label>
            <span>Run on</span>
            <select
              value={schedule.weekday}
              onChange={(event) => {
                if (schedule.frequency !== "weekly") return;
                onChange({
                  ...draft,
                  schedule: {
                    ...schedule,
                    weekday: event.currentTarget
                      .value as (typeof weekdays)[number],
                  },
                });
              }}
            >
              {weekdays.map((weekday) => (
                <option key={weekday} value={weekday}>
                  {weekday[0]?.toUpperCase()}
                  {weekday.slice(1)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {draft.retentionTestMinutes === 5 || draft.scheduleTestMinutes === 10 ? (
        <p className="schedule-devstage-note">
          Devstage test settings create short-lived backup cycles. They are not
          available in production.
        </p>
      ) : null}

      {cloudState.entitlements.effectivePlan === "pro" ? (
        <section className="schedule-entitlement-strip">
          <div>
            <p>Every 6 or 12 hours, and retention up to 30 days</p>
            <button onClick={onComparePlans} type="button">
              Compare plans
            </button>
          </div>
          <span className="feature-badge">Pro Extended</span>
        </section>
      ) : null}

      {cloudState.entitlements.billingAttention ? (
        <p className="billing-notice">
          Billing needs attention. Existing backups remain available, but this
          schedule may stop at renewal.
        </p>
      ) : null}

      <div className="schedule-actions">
        <button
          disabled={!dirty || saveState === "saving"}
          onClick={onSave}
          type="button"
        >
          {saveState === "saving" ? "Saving…" : "Save schedule"}
        </button>
        <button className="secondary-action" onClick={onCancel} type="button">
          Cancel
        </button>
      </div>
      <p
        className={`schedule-save-message${error ? " is-error" : ""}`}
        aria-live="polite"
      >
        {error ?? (saveState === "saved" ? "Schedule saved." : "")}
      </p>
    </section>
  );
}
